import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowLeft,
    Loader2,
    ChevronLeft,
    ChevronRight,
    CalendarCheck,
    X,
    Repeat,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_LABELS } from "@/lib/workoutTaxonomy";
import {
    DAY_LABELS,
    formatTimeDisplay,
    isoDate,
    startOfWeekMon,
    addDays,
    weekRangeLabel,
    jsDayToMonIdx,
} from "@/lib/schedule";
import ChangeRecurringModal from "@/components/parent/ChangeRecurringModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberSchedule() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    // `?athlete_id=X` deep-link (from ClientDashboard athlete tab) preselects
    // that kid in the booking modal so a parent doesn't have to repick.
    const [searchParams] = useSearchParams();
    const preselectedAthleteId = searchParams.get("athlete_id") || null;
    const [weekStart, setWeekStart] = useState(() => startOfWeekMon(new Date()));
    const [slots, setSlots] = useState([]);
    const [myBookings, setMyBookings] = useState([]);
    const [athletes, setAthletes] = useState([]);
    const [busy, setBusy] = useState(true);
    const [activeSlot, setActiveSlot] = useState(null);
    const [loadError, setLoadError] = useState(null);
    // Mobile: only render ONE day at a time so the page doesn't become a
    // 7-day vertical scroll on phones. 0=Mon ... 6=Sun. Defaults to today
    // when "this week" is in view; resets to Mon when paging weeks.
    // Sachin 2026-02-28: "make it so each DAY shows and not the whole week"
    const [selectedDayIdx, setSelectedDayIdx] = useState(() => {
        const today = new Date();
        return jsDayToMonIdx(today.getDay());
    });
    // Category filter — "all" or a CATEGORY key. Sachin 2026-02-28 audit:
    // "every interaction should be one tap away." Filter chips collapse the
    // grid to just the category the parent cares about.
    const [categoryFilter, setCategoryFilter] = useState("all");
    // Recurring templates for "Manage my recurring schedule" panel
    const [recurringTemplates, setRecurringTemplates] = useState([]);
    const [editingTemplate, setEditingTemplate] = useState(null);

    const loadRecurringTemplates = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/me/recurring-templates`, { withCredentials: true });
            setRecurringTemplates(r.data?.templates || []);
        } catch {
            setRecurringTemplates([]);
        }
    }, []);

    useEffect(() => {
        if (!loading && user && user.role === "client") loadRecurringTemplates();
    }, [loading, user, loadRecurringTemplates]);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role === "owner" || user.role === "coach") {
            navigate("/coach/schedule", { replace: true });
            return;
        }
        // Status is not the source of truth — let the backend gate. A user
        // can have active credits or future bookings even when the status
        // flag has drifted (coach manually marked non_member, etc). The
        // backend `/api/schedule/slots` will 403 if there's truly no
        // entitlement, and we render an empty-state from `loadError` below.
    }, [loading, user, navigate]);

    const weekFrom = useMemo(() => isoDate(weekStart), [weekStart]);
    const weekTo = useMemo(() => isoDate(addDays(weekStart, 6)), [weekStart]);

    const refresh = async () => {
        setBusy(true);
        setLoadError(null);
        try {
            // Load bookings + profile first — these aren't gated and we want
            // to show the member's existing schedule even if `/schedule/slots`
            // is locked down for some reason.
            const [m, p] = await Promise.all([
                axios.get(`${API}/schedule/my-bookings`, { withCredentials: true }),
                axios.get(`${API}/profile`, { withCredentials: true }),
            ]);
            setMyBookings(m.data || []);
            setAthletes((p.data?.athletes || []).map((a) => ({
                athlete_id: a.athlete_id,
                name: `${a.first_name} ${a.last_name}`.trim(),
            })));
            try {
                const s = await axios.get(`${API}/schedule/slots`, {
                    params: { from_date: weekFrom, to_date: weekTo },
                    withCredentials: true,
                });
                setSlots(s.data || []);
            } catch (slotErr) {
                // 403 → backend says no entitlement. Don't kick the user out —
                // their existing bookings are still visible and rescheduling
                // (cancel + book) still works once they have credits/sub.
                if (slotErr?.response?.status === 403) {
                    setSlots([]);
                    setLoadError(slotErr.response.data?.detail || "No active sessions on this account");
                } else {
                    throw slotErr;
                }
            }
        } catch (e) {
            toast.error("Couldn't load schedule", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (!user) return;
        if (user.role === "owner" || user.role === "coach") return;
        // Defer to a microtask so `react-hooks/set-state-in-effect` doesn't
        // flag the synchronous entry — refresh()'s setStates run after await.
        Promise.resolve().then(refresh);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, weekFrom, weekTo]);

    const myAthleteIdsBySlot = useMemo(() => {
        const map = {};
        myBookings.forEach((b) => {
            map[b.slot_id] = map[b.slot_id] || [];
            map[b.slot_id].push(b);
        });
        return map;
    }, [myBookings]);

    const slotsByDay = useMemo(() => {
        const out = Array.from({ length: 7 }, () => []);
        slots.forEach((sl) => {
            if (categoryFilter !== "all" && (sl.category || "_uncat") !== categoryFilter) return;
            const d = new Date(`${sl.date}T00:00:00`);
            const idx = jsDayToMonIdx(d.getDay());
            out[idx].push(sl);
        });
        out.forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
        return out;
    }, [slots, categoryFilter]);

    // What categories actually exist in the loaded slots, for the filter chips
    const availableCategories = useMemo(() => {
        const seen = new Map();  // key -> count
        slots.forEach((sl) => {
            const key = sl.category || "_uncat";
            seen.set(key, (seen.get(key) || 0) + 1);
        });
        return Array.from(seen.entries()).map(([key, count]) => ({ key, count }));
    }, [slots]);

    // Week-at-a-glance counts: how many slots the family has booked this week
    // vs how many open seats remain across all visible slots. Helps the parent
    // answer "am I done planning this week?" before they scroll the grid.
    const weekStats = useMemo(() => {
        const mySlotIds = new Set(Object.keys(myAthleteIdsBySlot));
        let bookedCount = 0;
        myBookings.forEach((b) => {
            if (b.date >= weekFrom && b.date <= weekTo) bookedCount += 1;
        });
        let openCount = 0;
        slotsByDay.forEach((arr) => {
            arr.forEach((s) => {
                if (mySlotIds.has(s.slot_id)) return;
                if (s.available) openCount += 1;
            });
        });
        return { bookedCount, openCount };
    }, [slotsByDay, myBookings, weekFrom, weekTo, myAthleteIdsBySlot]);

    // Find the next non-empty day from selectedDayIdx (for the empty-day jump)
    const nextNonEmptyDayIdx = useMemo(() => {
        for (let i = 1; i < 7; i++) {
            const idx = (selectedDayIdx + i) % 7;
            if (slotsByDay[idx].length > 0) return idx;
        }
        return null;
    }, [slotsByDay, selectedDayIdx]);

    // Next upcoming booking across all athletes — drives the "Next session" hero card.
    const nextBooking = useMemo(() => {
        const nowIso = new Date().toISOString();
        const upcoming = (myBookings || []).filter((b) => {
            if (!b.date || !b.start_time) return false;
            try {
                const slotIso = new Date(`${b.date}T${b.start_time}:00`).toISOString();
                return slotIso > nowIso;
            } catch { return false; }
        });
        upcoming.sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
        return upcoming[0] || null;
    }, [myBookings]);

    const book = async (slot, athleteId) => {
        try {
            await axios.post(
                `${API}/schedule/bookings`,
                { slot_id: slot.slot_id, athlete_id: athleteId },
                { withCredentials: true },
            );
            toast.success("Booked", { description: `${slot.date} at ${formatTimeDisplay(slot.start_time)}` });
            setActiveSlot(null);
            refresh();
        } catch (e) {
            toast.error("Couldn't book", { description: e?.response?.data?.detail });
        }
    };

    const cancelBooking = useCallback(async (booking_id, slot) => {
        // Model B cutoffs: >12h free · 1h-12h late cancel · <1h blocked.
        let prompt = "Cancel this booking?";
        if (slot?.date && slot?.start_time) {
            try {
                const slotMs = new Date(`${slot.date}T${slot.start_time}:00Z`).getTime();
                const hoursOut = (slotMs - Date.now()) / 3.6e6;
                if (hoursOut < 1) {
                    window.alert(
                        "Too late to cancel — sessions can't be cancelled within 1 hour of start.\n" +
                        "Contact your coach if you can't make it; they'll mark it as a no-show."
                    );
                    return;
                }
                if (hoursOut < 12) {
                    prompt = "\u26A0 This is a LATE CANCEL (within 12h of start). 2 late cancels in a calendar month = 1 paid session deducted. Cancel anyway?";
                }
            } catch { /* ignore */ }
        }
        if (!window.confirm(prompt)) return;
        try {
            const r = await axios.delete(`${API}/schedule/bookings/${booking_id}`, { withCredentials: true });
            const msg = r.data?.message || "Cancelled";
            if (r.data?.late_cancel) {
                if (r.data?.penalty?.penalised) {
                    toast.error("Late cancel — 1 session deducted", { description: msg, duration: 8000 });
                } else {
                    toast.warning("Late cancel recorded", { description: msg, duration: 7000 });
                }
            } else {
                toast.success("Cancelled — credit refunded", { description: msg });
            }
            refresh();
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 409) {
                toast.error("Too late to cancel", { description: detail, duration: 9000 });
            } else {
                toast.error("Couldn't cancel", { description: detail });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading || busy) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-brand-red" />
            </div>
        );
    }

    return (
        <div data-testid="member-schedule" className="min-h-screen bg-black text-white">
            <div className="max-w-6xl mx-auto px-4 pt-28 pb-10">
                <div className="flex items-center gap-3 mb-6">
                    <Link to="/dashboard" className="text-zinc-200 hover:text-white inline-flex items-center gap-1">
                        <ArrowLeft className="w-4 h-4" /> Dashboard
                    </Link>
                </div>

                <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Schedule</h1>
                <p className="mt-2 text-zinc-200">
                    Book your athletes into open training slots. Tap a slot to book; you can cancel up to your coach&apos;s cutoff window before the session starts.
                </p>

                {/* Next session hero — top-of-page answer to "when's our next
                    session?" Sachin 2026-02-28: highest-asked question on
                    this page; surface it instead of burying in the list. */}
                {nextBooking && (
                    <div
                        data-testid="schedule-next-session-hero"
                        className="mt-6 rounded-2xl border border-brand-red/60 bg-gradient-to-br from-brand-red/15 to-zinc-950/40 p-5 flex items-start justify-between gap-4 flex-wrap"
                    >
                        <div className="min-w-0">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-1.5">
                                Your next session
                            </div>
                            <div className="font-display text-2xl sm:text-3xl tracking-wider text-white">
                                {new Date(`${nextBooking.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                            </div>
                            <div className="mt-1 text-sm text-zinc-200">
                                {formatTimeDisplay(nextBooking.start_time)} · {nextBooking.athlete_name || "Athlete"}
                                {nextBooking.label ? ` · ${nextBooking.label}` : (nextBooking.category ? ` · ${CATEGORY_LABELS[nextBooking.category]}` : "")}
                                {" · "}{nextBooking.duration_minutes}min
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="schedule-cancel-next-btn"
                            onClick={() => cancelBooking(nextBooking.booking_id, nextBooking)}
                            className="text-xs px-3 py-2 rounded-lg border border-zinc-700 text-zinc-100 hover:border-brand-red hover:text-white whitespace-nowrap"
                            title="Cancel this booking"
                        >
                            Cancel session
                        </button>
                    </div>
                )}

                {loadError && (
                    <div
                        data-testid="schedule-locked-banner"
                        className="mt-6 border border-amber-700/60 bg-amber-950/30 rounded-2xl p-4 text-sm text-amber-100"
                    >
                        <div className="font-semibold mb-1">Booking is locked on this account</div>
                        <div className="text-amber-200/90 text-xs">
                            {loadError}. Your existing sessions are shown below — reach out to your coach to keep training.
                        </div>
                    </div>
                )}

                {myBookings.length > 0 && (
                    <div data-testid="my-bookings" className="mt-8 border border-brand-navy-800/60 bg-brand-navy-900/20 rounded-2xl p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <CalendarCheck className="w-4 h-4 text-brand-navy-300" />
                            <h2 className="text-lg font-semibold">Upcoming sessions</h2>
                        </div>
                        <div className="divide-y divide-brand-navy-900/60">
                            {myBookings.map((b) => (
                                <div key={b.booking_id} data-testid={`my-booking-${b.booking_id}`} className="py-3 flex items-center justify-between gap-4">
                                    <div className="text-sm">
                                        <div className="font-semibold">
                                            {new Date(`${b.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                                            {" · "}{formatTimeDisplay(b.start_time)}
                                            {b.recurring_template_id && (
                                                <span className="ml-2 inline-flex items-center gap-1 text-[9px] tracking-wider uppercase text-emerald-400 bg-emerald-950/40 border border-emerald-900/50 rounded px-1.5 py-0.5">
                                                    <Repeat className="w-2.5 h-2.5" /> recurring
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-zinc-200 text-xs mt-0.5">
                                            {b.athlete_name || "Athlete"} · {b.duration_minutes}min
                                            {b.label ? ` · ${b.label}` : (b.category ? ` · ${CATEGORY_LABELS[b.category]}` : "")}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {b.recurring_template_id && (
                                            <button
                                                type="button"
                                                data-testid={`change-recurring-btn-${b.booking_id}`}
                                                onClick={() => {
                                                    const tpl = recurringTemplates.find((t) => t.template_id === b.recurring_template_id);
                                                    if (tpl) setEditingTemplate({ template: tpl, booking: b });
                                                    else toast.info("Recurring template not found — try refreshing.");
                                                }}
                                                className="text-xs text-emerald-300 hover:text-emerald-200"
                                            >
                                                Change
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            data-testid={`cancel-booking-${b.booking_id}`}
                                            onClick={() => cancelBooking(b.booking_id, b)}
                                            className="text-xs text-zinc-200 hover:text-brand-red"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Manage recurring schedule — Sachin spec 2026-02-17 */}
                {recurringTemplates.length > 0 && (
                    <div data-testid="manage-recurring-panel" className="mt-6 border border-emerald-900/40 bg-emerald-950/10 rounded-2xl p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <Repeat className="w-4 h-4 text-emerald-400" />
                            <h2 className="text-lg font-semibold">Recurring schedule</h2>
                        </div>
                        <p className="text-xs text-zinc-300 mb-4">
                            Your standing weekly sessions. Tap to change day/time — capacity is checked before commit.
                        </p>
                        <div className="space-y-2">
                            {recurringTemplates.map((t) => (
                                <button
                                    type="button"
                                    key={t.template_id}
                                    data-testid={`recurring-template-${t.template_id}`}
                                    onClick={() => setEditingTemplate({ template: t, booking: null })}
                                    className="w-full text-left bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 transition-colors flex items-center justify-between gap-3"
                                >
                                    <div>
                                        <div className="text-sm font-semibold text-white">
                                            {DAY_LABELS[t.day_of_week]}s at {formatTimeDisplay(t.start_time)}
                                        </div>
                                        <div className="text-xs text-zinc-400 mt-0.5">
                                            {t.athlete_name || "Athlete"}{t.label ? ` · ${t.label}` : ""}
                                        </div>
                                    </div>
                                    <span className="text-xs text-emerald-300">Change →</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-10 border border-zinc-800 rounded-2xl bg-zinc-950/50">
                    <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            data-testid="member-week-prev"
                            onClick={() => { setWeekStart((d) => addDays(d, -7)); setSelectedDayIdx(0); }}
                            className="p-2 hover:text-brand-red"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setWeekStart(startOfWeekMon(new Date()));
                                setSelectedDayIdx(jsDayToMonIdx(new Date().getDay()));
                            }}
                            className="text-xs px-3 py-1 rounded-full border border-zinc-700 hover:border-brand-red hover:text-brand-red"
                        >
                            This week
                        </button>
                        <button
                            type="button"
                            data-testid="member-week-next"
                            onClick={() => { setWeekStart((d) => addDays(d, 7)); setSelectedDayIdx(0); }}
                            className="p-2 hover:text-brand-red"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <h2 className="ml-2 text-lg font-semibold">{weekRangeLabel(weekStart)}</h2>
                        <span
                            data-testid="schedule-week-stats"
                            className="ml-auto text-[11px] text-zinc-300"
                        >
                            <span className="text-white font-semibold">{weekStats.bookedCount}</span> booked
                            {" · "}
                            <span className="text-white font-semibold">{weekStats.openCount}</span> open
                        </span>
                    </div>

                    {/* Category filter chips — only show when there are 2+
                        distinct categories in the loaded slots, otherwise the
                        filter is noise. */}
                    {availableCategories.length > 1 && (
                        <div
                            data-testid="schedule-category-filters"
                            className="px-3 py-2 border-b border-zinc-800 flex items-center gap-1.5 overflow-x-auto"
                        >
                            <button
                                type="button"
                                data-testid="cat-filter-all"
                                onClick={() => setCategoryFilter("all")}
                                className={`text-[11px] px-3 py-1 rounded-full border whitespace-nowrap ${
                                    categoryFilter === "all"
                                        ? "bg-brand-red border-brand-red text-white"
                                        : "border-zinc-700 text-zinc-200 hover:border-zinc-500"
                                }`}
                            >
                                All · {slots.length}
                            </button>
                            {availableCategories.map(({ key, count }) => (
                                <button
                                    key={key}
                                    type="button"
                                    data-testid={`cat-filter-${key}`}
                                    onClick={() => setCategoryFilter(key)}
                                    className={`text-[11px] px-3 py-1 rounded-full border whitespace-nowrap ${
                                        categoryFilter === key
                                            ? "bg-brand-red border-brand-red text-white"
                                            : "border-zinc-700 text-zinc-200 hover:border-zinc-500"
                                    }`}
                                >
                                    {key === "_uncat" ? "Other" : (CATEGORY_LABELS[key] || key)} · {count}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Mobile-only day picker — 7 small day pills so the user
                        flips through one day at a time instead of scrolling a
                        7-day vertical list. Desktop keeps the 7-col grid below. */}
                    <div data-testid="member-day-picker" className="md:hidden px-3 py-3 border-b border-zinc-800 overflow-x-auto">
                        <div className="flex gap-2 min-w-max">
                            {DAY_LABELS.map((label, idx) => {
                                const day = addDays(weekStart, idx);
                                const isSelected = selectedDayIdx === idx;
                                const isToday = isoDate(day) === isoDate(new Date());
                                const hasSlots = slotsByDay[idx].length > 0;
                                return (
                                    <button
                                        key={label}
                                        type="button"
                                        data-testid={`member-day-pill-${idx}`}
                                        onClick={() => setSelectedDayIdx(idx)}
                                        className={`flex-1 min-w-[3.25rem] flex flex-col items-center justify-center rounded-xl px-2 py-2 border transition-colors ${
                                            isSelected
                                                ? "border-brand-red bg-brand-red text-white"
                                                : "border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-zinc-500"
                                        }`}
                                    >
                                        <span className="text-[10px] tracking-wider uppercase opacity-90">{label.slice(0, 3)}</span>
                                        <span className={`text-base font-bold leading-tight ${isToday && !isSelected ? "text-brand-red" : ""}`}>
                                            {day.getDate()}
                                        </span>
                                        <span className={`mt-0.5 w-1.5 h-1.5 rounded-full ${hasSlots ? (isSelected ? "bg-white" : "bg-emerald-400") : "bg-transparent"}`} />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-7 md:divide-x md:divide-zinc-800 md:divide-y-0">
                        {DAY_LABELS.map((label, idx) => {
                            const day = addDays(weekStart, idx);
                            const isEmpty = slotsByDay[idx].length === 0;
                            // On mobile: ONLY the selected day renders. Desktop: all 7.
                            const mobileHidden = selectedDayIdx !== idx;
                            const isSelected = selectedDayIdx === idx;
                            return (
                                <div
                                    key={label}
                                    data-testid={`member-day-col-${idx}`}
                                    className={`md:min-h-[18rem] flex-col ${mobileHidden ? "hidden md:flex" : "flex"} ${isEmpty ? "md:flex" : ""}`}
                                >
                                    <div className={`px-3 py-2 border-b md:sticky md:top-0 bg-zinc-950/80 backdrop-blur flex md:block items-baseline gap-2 ${
                                        isSelected ? "border-brand-red/60" : "border-zinc-800"
                                    }`}>
                                        <div className={`text-xs uppercase tracking-wider ${
                                            isSelected ? "text-brand-red" : "text-zinc-300"
                                        }`}>{label}</div>
                                        <div className="text-sm font-semibold">{day.getDate()}</div>
                                    </div>
                                    <div className="flex-1 px-2 py-2 space-y-2">
                                        {slotsByDay[idx].length === 0 ? (
                                            <div
                                                data-testid={`member-day-empty-${idx}`}
                                                className="text-xs text-zinc-400 px-1 py-6 text-center md:text-left md:py-2"
                                            >
                                                No sessions scheduled this day.
                                                {/* Mobile-only jump-link to the next day that DOES have slots
                                                    so the parent isn't stuck on a dead-end empty pill. */}
                                                {isSelected && nextNonEmptyDayIdx !== null && (
                                                    <button
                                                        type="button"
                                                        data-testid="member-empty-jump"
                                                        onClick={() => setSelectedDayIdx(nextNonEmptyDayIdx)}
                                                        className="md:hidden mt-3 mx-auto block text-xs text-brand-red hover:text-white underline"
                                                    >
                                                        {DAY_LABELS[nextNonEmptyDayIdx]} has {slotsByDay[nextNonEmptyDayIdx].length} session{slotsByDay[nextNonEmptyDayIdx].length === 1 ? "" : "s"} →
                                                    </button>
                                                )}
                                            </div>
                                        ) : slotsByDay[idx].map((s) => {
                                            const mine = (myAthleteIdsBySlot[s.slot_id] || []).length > 0;
                                            const disabled = !mine && !s.available;
                                            return (
                                                <button
                                                    key={s.slot_id}
                                                    type="button"
                                                    data-testid={`member-slot-${s.slot_id}`}
                                                    onClick={() => !disabled && setActiveSlot(s)}
                                                    disabled={disabled}
                                                    style={{ touchAction: "manipulation" }}
                                                    className={`w-full text-left rounded-lg border px-3 py-2.5 md:px-2 md:py-1.5 transition-colors active:opacity-80 ${
                                                        mine
                                                            ? "border-brand-navy-600 bg-brand-navy-900/40"
                                                            : !s.available
                                                                ? "border-zinc-800 bg-zinc-900/40 text-zinc-200 cursor-not-allowed"
                                                                : "border-zinc-700 bg-zinc-900 hover:border-brand-red"
                                                    }`}
                                                >
                                                    <div className="text-sm font-semibold">{formatTimeDisplay(s.start_time)}</div>
                                                    <div className="text-[11px] truncate">
                                                        {s.label || (s.category ? CATEGORY_LABELS[s.category] : `${s.duration_minutes}min`)}
                                                    </div>
                                                    <div className="text-[11px] mt-0.5">
                                                        {mine ? <span className="text-brand-navy-300">Booked</span> : (
                                                            s.available
                                                                ? <span className="text-zinc-200">Available</span>
                                                                : <span className="text-zinc-200">Full</span>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {activeSlot && (
                <BookSlotModal
                    slot={activeSlot}
                    athletes={athletes}
                    myBookingsForSlot={myAthleteIdsBySlot[activeSlot.slot_id] || []}
                    preselectedAthleteId={preselectedAthleteId}
                    onBook={book}
                    onCancel={cancelBooking}
                    onClose={() => setActiveSlot(null)}
                />
            )}

            {editingTemplate && (
                <ChangeRecurringModal
                    booking={editingTemplate.booking}
                    template={editingTemplate.template}
                    onClose={() => setEditingTemplate(null)}
                    onCommitted={() => { refresh(); loadRecurringTemplates(); }}
                />
            )}
        </div>
    );
}

function BookSlotModal({ slot, athletes, myBookingsForSlot, preselectedAthleteId, onBook, onCancel, onClose }) {
    const bookedAthIds = new Set(myBookingsForSlot.map((b) => b.athlete_id));
    const available = athletes.filter((a) => !bookedAthIds.has(a.athlete_id));
    // Honor `?athlete_id=` deep-link from ClientDashboard if the athlete is
    // available for this slot; otherwise default to the first available.
    const initialPick =
        (preselectedAthleteId && available.some((a) => a.athlete_id === preselectedAthleteId))
            ? preselectedAthleteId
            : (available[0]?.athlete_id || "");
    const [pick, setPick] = useState(initialPick);

    return (
        <div data-testid="member-book-modal" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                        {new Date(`${slot.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                        {" · "}{formatTimeDisplay(slot.start_time)}
                    </h3>
                    <button type="button" onClick={onClose} className="text-zinc-200 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="text-sm text-zinc-200">
                        {slot.label || (slot.category ? CATEGORY_LABELS[slot.category] : "Session")} · {slot.duration_minutes}min
                        <div className="text-xs mt-1">Cancel up to {slot.cancel_window_hours}h before start.</div>
                    </div>

                    {myBookingsForSlot.length > 0 && (
                        <div className="border border-brand-navy-800/60 bg-brand-navy-900/20 rounded-lg p-3 space-y-2">
                            <div className="text-xs text-brand-navy-300 uppercase tracking-wider">Booked</div>
                            {myBookingsForSlot.map((b) => (
                                <div key={b.booking_id} className="flex items-center justify-between text-sm">
                                    <span>{b.athlete_name}</span>
                                    <button
                                        type="button"
                                        data-testid={`modal-cancel-${b.booking_id}`}
                                        onClick={() => onCancel(b.booking_id)}
                                        className="text-xs text-zinc-200 hover:text-brand-red"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {available.length > 0 ? (
                        <>
                            <label className="block">
                                <span className="text-xs uppercase tracking-wider text-zinc-300">Book athlete</span>
                                <select
                                    data-testid="member-book-athlete"
                                    className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                                    value={pick}
                                    onChange={(e) => setPick(e.target.value)}
                                >
                                    {available.map((a) => (<option key={a.athlete_id} value={a.athlete_id}>{a.name}</option>))}
                                </select>
                            </label>
                            <button
                                type="button"
                                data-testid="member-book-submit"
                                onClick={() => onBook(slot, pick)}
                                className="btn-tactical w-full"
                            >
                                Confirm booking
                            </button>
                        </>
                    ) : athletes.length === 0 ? (
                        <p className="text-sm text-zinc-300">Add an athlete in your profile to book a session.</p>
                    ) : (
                        <p className="text-sm text-zinc-300">All your athletes are already booked into this slot.</p>
                    )}
                </div>
            </div>
        </div>
    );
}
